import { isEmpty } from 'lodash-es';
import type { Interpolation } from 'styled-components';
import type { Simplify } from 'type-fest';

import { parsedUserAgent } from 'utils/deviceInfoUtils';

export type NonTransientProp<T> = Simplify<{
  [K in keyof T as K extends `$${infer Rest}` ? Rest : K]: T[K];
}>;

export type TransientProps<T> = Simplify<{
  [K in keyof T as `$${K & string}`]: T[K];
}>;

export const variants =
  <VariantEnum extends number | string>(
    propName: `$${string}`,
    variants: { [key in VariantEnum]: Interpolation<object> },
    defaultVariantName?: VariantEnum
  ) =>
  props => {
    const propVariant = props[propName];
    const variantName = isEmpty(propVariant) ? defaultVariantName : propVariant;

    return variantName === undefined ? null : variants[variantName];
  };

/**
 * Similar to the above util, this function conditionally applies CSS to all browsers that are not Safari under version
 * 14. This is to fix an issue with TranslateScrollable component scrollbars failing to render on versions of Safari
 * under 14, due to the CSS property will-change: transform being applied to the scrollbar container. However, on other
 * browsers, this CSS property greatly improves scrolling performance so we'd still like to include it.
 */
export const nonSafari13BrowserStyles =
  (css: TemplateStringsArray, ...rest: any[]) =>
  (): string | TemplateStringsArray => {
    if (!(parsedUserAgent.browser.name === 'Safari' && Number(parsedUserAgent.browser.version) < 14)) {
      const flatString = css.map((item, index) => [...item, rest?.[index] || '']).join('');

      return flatString;
    }

    return '';
  };

export const hexToRGBA = (color: string, alpha?: string): string => {
  if (!color || !color.trim()) {
    return 'transparent';
  }

  if (!color.startsWith('#')) {
    return alpha ? `rgb(from ${color} r g b / ${alpha})` : `rgb(from ${color} r g b)`;
  }

  const parseHex = color.replace('#', '');
  if (parseHex.length !== 3 && parseHex.length !== 6) {
    return 'transparent';
  }

  const r = Number.parseInt(parseHex.length === 3 ? parseHex.slice(0, 1).repeat(2) : parseHex.slice(0, 2), 16);
  const g = Number.parseInt(parseHex.length === 3 ? parseHex.slice(1, 2).repeat(2) : parseHex.slice(2, 4), 16);
  const b = Number.parseInt(parseHex.length === 3 ? parseHex.slice(2, 3).repeat(2) : parseHex.slice(4, 6), 16);

  return alpha ? `rgba(${r}, ${g}, ${b}, ${alpha})` : `rgb(${r}, ${g}, ${b})`;
};

/**
 * Negates a CSS length value for use in styled-components template literals.
 *
 * Needed because tokens are now CSS custom properties (e.g. `var(--space-200)`),
 * and `-var(--space-200)` is invalid CSS — the browser drops the declaration.
 * Wrap the token in `calc(... * -1)` instead.
 *
 * @example
 *   styled.div`top: ${negate(SPACE_200)};`
 *   // → `top: calc(var(--space-200) * -1);`
 */
export const negate = (value: string): string => `calc(${value} * -1)`;

/**
 * Converts a value from rem to pixels (px) based on the current root font size.
 * @param {string} rem - The value in rem (e.g., "0.5625rem") to be converted to pixels.
 * @returns {number} The converted value in pixels.
 */
export const remToPx = (rem: string): number => {
  const numericValue = Number.parseFloat(rem || '0');

  const baseFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize || '16px');

  const px = numericValue * baseFontSize;

  return px;
};

/**
 * Resolves a CSS custom property reference (e.g. `var(--font-weight-regular)`)
 * to its computed value on `:root`. Returns the input unchanged if it is not a
 * `var(...)` reference. Browser-only — safe inside Storybook MDX/stories.
 */
export const resolveCssVar = (value: number | string): string => {
  const string_ = String(value);
  const match = string_.match(/^var\((--[^,)]+)(?:,.*)?\)$/);
  if (!match) {
    return string_;
  }

  return getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim() || string_;
};

export const isStringCoercibleToNumber = (candidate: string) => !Number.isNaN(Number(candidate));

/**
 * Coerce a string value with units (ex. "10px" or "2.125rem") to a unitless value (ex. "10" or "2.125").
 * Fallback to zero.
 */
export const stripUnits = (value: string): number => {
  const match = value.match(/\d+(\.\d+)?/)?.[0];

  return match && isStringCoercibleToNumber(match) ? Number(match) : 0;
};
